import {
  AspectRatio,
  Box,
  Card,
  Flex,
  FormatNumber,
  Heading,
  HStack,
  Image,
  SimpleGrid,
  Skeleton,
  Spacer,
  Text,
  VStack,
} from "@chakra-ui/react";
import { LottieReact } from "@lottielab/lottie-player";
import { useLocalStorage } from "usehooks-ts";
import { Address, erc20Abi, formatUnits } from "viem";
import { useReadContract } from "wagmi";

import { ActionsSection } from "./ActionsSection";
import { HomepageRightCard } from "./homepage-right-card";
import { UserProfileCard } from "./user-profile-card";

import { ChinaBanner } from "@/components/banners/china-banner";
import { ConnectWalletButton } from "@/components/connect-wallet-button";
import { NavButton } from "@/components/nav-button";
import { ReferralBanner } from "@/components/referral-banner";
import { RoundedBadge } from "@/components/rounded-badge";
import { RoundedButton } from "@/components/rounded-button";
import { PUSD_URL, usdceTokenAddress } from "@/constants/shared";
import { useAuthenticatedLoadingState } from "@/hooks/useAuthenticatedLoadingState";
import { useCompliance } from "@/hooks/useCompliance";
import { useCurrentUserData } from "@/hooks/useCurrentUserData";

export const HomeHeaderSection = () => {
  const { authenticatedLoadingState } = useAuthenticatedLoadingState();
  const { currentUserData, isLoading: isLoadingCurrentUserData } =
    useCurrentUserData();
  if (authenticatedLoadingState && isLoadingCurrentUserData) {
    return (
      <VStack gap={4} w="full">
        <SimpleGrid columns={[1, 1, 1, 1, 2]} gap={4} w="full">
          <Skeleton h={[286, 286, 286, 235]} borderRadius="2xl" />
          <Skeleton h={[286, 286, 286, 235]} borderRadius="2xl" />
        </SimpleGrid>
        <SimpleGrid columns={[1, 1, 2, 2, 4]} w="full" gap={4}>
          <Skeleton h="184px" borderRadius="2xl" />
          <Skeleton h="184px" borderRadius="2xl" />
          <Skeleton h="184px" borderRadius="2xl" />
          <Skeleton h="184px" borderRadius="2xl" />
        </SimpleGrid>
      </VStack>
    );
  }

  if (currentUserData) {
    return (
      <VStack gap={4} w="full">
        <AirdropRegistrationBanner />
        <HeaderBanner />
        <SimpleGrid columns={[1, 1, 1, 1, 2]} gap={4} w="full">
          <UserProfileCard />
          <HomepageRightCard />
        </SimpleGrid>
        <ActionsSection />
      </VStack>
    );
  }

  return (
    <VStack gap={4} w="full">
      <ReferralBanner />

      <VStack
        py={8}
        px={6}
        w="full"
        background="radial-gradient(125.86% 243.99% at 50% 21.56%, rgba(211, 211, 211, 0.01) 0%, #93E6FF 99.99%)"
        hideFrom="md"
        gap={4}
        alignItems="center"
        textAlign="center"
        rounded="24px"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="43"
          height="43"
          viewBox="0 0 43 43"
          fill="none"
        >
          <path
            d="M28.3114 15.3894V26.7856L23.8377 22.2748V10.8786L28.3114 15.3894Z"
            fill="#FF3D00"
          />
          <path
            d="M15.8391 27.9717H27.1356L22.6642 23.4585H11.3677L15.8391 27.9717Z"
            fill="#FF3D00"
          />
          <path
            d="M34.6697 34.426V21.8039L30.1961 17.293V29.9151L17.7261 29.873L22.1975 34.3861L34.6673 34.4283"
            fill="#FF3D00"
          />
          <path
            d="M5.62451 5.29407L5.62451 17.9161L10.0982 22.427L10.0982 9.80491L22.5682 9.84704L18.0968 5.33388L5.62689 5.29175"
            fill="#FF3D00"
          />
          <path
            d="M35.8525 36.8231L33.3144 34.2626L34.5472 33.0189L37.0853 35.5794L37.3753 37.0426L35.8525 36.8231Z"
            fill="#FF3D00"
          />
        </svg>

        <Heading fontSize="40px" fontWeight="700" lineHeight="48px">
          RWAs That Feel Just Like Crypto
        </Heading>
        <Heading size="xl" color="text.secondary" fontWeight="500">
          Join Plume in bringing the real world to crypto
        </Heading>
        <Box mt={2}>
          <ConnectWalletButton />
        </Box>
      </VStack>
      <Box hideFrom="md" w="full">
        <ActionsSection />
      </Box>
      <Box
        position="relative"
        borderRadius="3xl"
        overflow="hidden"
        bgColor="bg.subtle"
        w="100%"
        hideBelow="md"
      >
        <VStack
          position="absolute"
          left="50%"
          top="50%"
          transform="translate(-50%,-50%)"
          textAlign="center"
          zIndex={1}
          w="100%"
        >
          <Image src="/assets/logo-feather-red.svg" hideBelow="lg" />
          <Heading size="5xl" maxW="449px" fontWeight="700">
            RWAs That Feel Just Like Crypto
          </Heading>
          <Heading size="xl" color="fg.muted" mb={4} fontWeight="500">
            Join Plume in bringing the real world to crypto
          </Heading>
          <ConnectWalletButton />
        </VStack>
        <AspectRatio ratio={1166 / 410}>
          <LottieReact src="https://cdn.lottielab.com/l/9VLP6b8Bfj6JJb.json" />
        </AspectRatio>
      </Box>
    </VStack>
  );
};

const AirdropRegistrationBanner = () => {
  return (
    <Flex
      bg="#236B53"
      rounded="2xl"
      w="full"
      overflow="hidden"
      px={[4, 6, 8]}
      py={6}
      gap={6}
      alignItems={["flex-start", "flex-start", "center"]}
      flexDir={["column", "column", "row"]}
    >
      <HStack gap={6} flex="1" minW={0} alignItems="center">
        <Box as="span" boxSize="56px" flexShrink={0} color="fg.inverted">
          <svg
            width="56"
            height="56"
            viewBox="0 0 56 56"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="56" height="56" rx="28" fill="#00D594" />
            <path
              d="M34 22.4768C33.8897 22.4191 33.8111 22.3369 33.7578 22.2342L28 9L22.2421 22.2347C22.1891 22.3369 22.1099 22.4191 21.9996 22.4771L8.76501 28L21.9999 33.5229C22.1043 33.5671 22.1868 33.6452 22.2426 33.765L28 47L33.758 33.7653C33.8135 33.6452 33.896 33.5671 34.0004 33.5225L47.235 28L34 22.4768ZM28 31.9536C25.8177 31.9536 24.047 30.1829 24.047 28C24.047 25.8171 25.8173 24.0464 28 24.0464C30.1827 24.0464 31.953 25.8171 31.953 28C31.953 30.1829 30.1823 31.9536 28 31.9536Z"
              fill="currentColor"
            />
          </svg>
        </Box>
        <Box minW={0}>
          <Text
            color="fg.inverted"
            fontSize="md"
            lineHeight="24px"
            fontWeight="500"
            letterSpacing="-0.18px"
          >
            Plume Points Airdrop Season 2 claiming has ended
          </Text>
          <Text
            color="fg.inverted"
            opacity={0.8}
            fontSize="sm"
            lineHeight="20px"
            fontWeight="500"
            letterSpacing="-0.09px"
          >
            The claim period ended September 3, 2026 at 12:00 UTC. Thank you to
            everyone who participated.
          </Text>
        </Box>
      </HStack>
    </Flex>
  );
};

const HeaderBanner = () => {
  const { geoCompliance } = useCompliance();
  const { address: walletAddress } = useCurrentUserData();
  const [headerBannerDismissed, setHeaderBannerDismissed] = useLocalStorage(
    "header-banner-dismissed",
    false,
  );
  /* GET USDC.e BALANCE */
  const { data: balance } = useReadContract({
    abi: erc20Abi,
    address: usdceTokenAddress,
    functionName: "balanceOf",
    args: [walletAddress as Address],
  });
  const { data: decimals } = useReadContract({
    abi: erc20Abi,
    address: usdceTokenAddress,
    functionName: "decimals",
  });
  const usdcValue =
    balance && decimals ? Number(formatUnits(balance, decimals)) : 0;
  const onDismiss = () => {
    setHeaderBannerDismissed(true);
  };
  if (geoCompliance?.countryCode === "CN") {
    return <ChinaBanner />;
  }
  if (usdcValue < 1) {
    return null;
  }
  if (headerBannerDismissed) {
    return null;
  }
  return (
    <Card.Root
      rounded="2xl"
      bg="#0F261D"
      w="full"
      display="flex"
      flexDir="row"
      overflow="hidden"
    >
      <Image
        src="/assets/home/pusd-banner.avif"
        width="206px"
        height="128px"
        aspectRatio="103/64"
      />
      <Card.Body
        fontWeight="500"
        fontFamily="heading"
        justifyContent="space-between"
        alignItems="center"
        w="full"
        display="flex"
        flexDir="row"
        py={8}
        pl={0}
        pr={8}
      >
        <Box>
          <HStack>
            <Text color="fg.inverted" fontSize="xl">
              Upgrade your USDC to pUSD
            </Text>
            <RoundedBadge bg="#FFFFFF1A" color="fg.inverted">
              <Image src="/assets/tokens/usdc.svg" boxSize={4} />{" "}
              <FormatNumber value={usdcValue} />{" "}
              <Text color="fg.muted">USDC</Text>
            </RoundedBadge>
          </HStack>
          <Text color="fg.muted" fontSize="lg">
            pUSD is the native stablecoin for Plume. Fully backed and audited.
          </Text>
        </Box>
        <Spacer />
        <RoundedButton
          bg="#273C34"
          color="#FFFFFFB2"
          mr={2}
          onClick={onDismiss}
        >
          Dismiss
        </RoundedButton>
        <NavButton href={PUSD_URL} target="_blank" direction="fwd">
          Upgrade
        </NavButton>
      </Card.Body>
    </Card.Root>
  );
};
